import React, { useEffect, useState, useCallback } from "react";
import {
  Button,
  Card,
  CardBody,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
  UncontrolledDropdown,
} from "reactstrap";
import classnames from "classnames";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { DateTime } from "components/common/Date";
import MaterialRequestCancelConfirmModal from "../components/MaterialRequestCancelConfirmModal";
import DeleteModal from "components/common/DeleteModal";
import StatusBadge from "components/common/StatusBadge";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import {
  cancelMaterialRequest,
  deleteMaterialRequest,
} from "../services/MaterialRequestService";
import { MaterialRequestStatusMap } from "../constants/material-request.constants";
import {
  materialRequestDisplayCode,
  materialRequestEligibleForApproveReject,
  materialRequestEligibleForCancel,
  materialRequestEligibleForDelete,
  materialRequestEligibleForEdit,
} from "../utils/material-request.utils";
import MaterialRequestApproveRejectModals from "../components/MaterialRequestApproveRejectModals";
import { invalidatePendingMaterialRequestCount } from "../materialRequestPendingCountInvalidate";
import { useMaterialRequestOverview } from "./MaterialRequestOverviewContext";
import OverviewTab from "./OverviewTab";
import LineItemsTab from "./LineItemsTab";
import {
  MaterialRequestOverviewTabParam,
  isValidMaterialRequestOverviewTabParam,
  materialRequestOverviewTabIdFromSearchParam,
  searchParamForMaterialRequestOverviewTabId,
} from "./overviewTabSearchParams";

const Section = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { materialRequest: record, refetch } = useMaterialRequestOverview();
  const { can } = useAccess();
  const navigate = useNavigate();

  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const canApprove = can(Permissions.MATERIAL_REQUEST.APPROVE);
  const canReject = can(Permissions.MATERIAL_REQUEST.REJECT);

  const tabQuery = searchParams.get("tab");

  useEffect(() => {
    if (tabQuery && !isValidMaterialRequestOverviewTabParam(tabQuery)) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("tab");
          return next;
        },
        { replace: true },
      );
    }
  }, [tabQuery, setSearchParams]);

  if (!record) {
    return (
      <Row>
        <Col lg={12}>
          <p className="text-muted mb-2">
            This material request could not be loaded.
          </p>
          <Link
            to="/inventory-management/material-requests"
            className="btn btn-light btn-sm"
          >
            Back to material requests
          </Link>
        </Col>
      </Row>
    );
  }

  const displayTitle = materialRequestDisplayCode(record);
  const canApproveRejectUi = materialRequestEligibleForApproveReject(record);
  const canEditMr =
    can(Permissions.MATERIAL_REQUEST.UPDATE) &&
    materialRequestEligibleForEdit(record);
  const canCancelMr =
    can(Permissions.MATERIAL_REQUEST.CANCEL) &&
    materialRequestEligibleForCancel(record);
  const canDeleteMr =
    can(Permissions.MATERIAL_REQUEST.DELETE) &&
    materialRequestEligibleForDelete(record);

  const handleDelete = useCallback(async () => {
    if (!record.id) return;
    try {
      const res = await deleteMaterialRequest(record.id);
      if (res.success) {
        toast.success(res.message ?? "Material request deleted.");
        navigate("/inventory-management/material-requests", { replace: true });
      } else {
        toast.error(res.message ?? "Delete failed.");
      }
    } catch (err) {
      toast.error(
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "Delete failed.",
      );
    }
  }, [record.id, navigate]);

  const handleCancel = async () => {
    if (!record.id) return;
    setCancelSubmitting(true);
    try {
      const res = await cancelMaterialRequest(record.id);
      if (res.success) {
        toast.success(res.message ?? "Material request cancelled.");
        invalidatePendingMaterialRequestCount();
        setCancelOpen(false);
        await refetch();
      } else {
        toast.error(res.message ?? "Cancel failed.");
      }
    } catch (err) {
      toast.error(
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "Cancel failed.",
      );
    } finally {
      setCancelSubmitting(false);
    }
  };

  const activeTab = materialRequestOverviewTabIdFromSearchParam(tabQuery);

  const setTabInUrl = (tabId: string) => {
    const param = searchParamForMaterialRequestOverviewTabId(tabId);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (!param || param === MaterialRequestOverviewTabParam.OVERVIEW) {
          next.delete("tab");
        } else {
          next.set("tab", param);
        }
        return next;
      },
      { replace: true },
    );
  };

  return (
    <React.Fragment>
      <DeleteModal
        show={deleteOpen}
        onDeleteClick={() => void handleDelete()}
        onCloseClick={() => setDeleteOpen(false)}
      />
      <MaterialRequestApproveRejectModals
        record={record}
        approveOpen={approveOpen}
        rejectOpen={rejectOpen}
        onCloseApprove={() => setApproveOpen(false)}
        onCloseReject={() => setRejectOpen(false)}
        onSuccess={() => void refetch()}
      />
      <MaterialRequestCancelConfirmModal
        show={cancelOpen}
        submitting={cancelSubmitting}
        onClose={() => setCancelOpen(false)}
        onConfirm={handleCancel}
      />

      <Row>
        <Col lg={12}>
          <Card className="mt-n4 mx-n4">
            <div className="bg-light-subtle">
              <CardBody className="pb-0 px-4">
                <Row className="mb-3 align-items-start">
                  <div className="col-md">
                    <div>
                      <h4 className="fw-bold">{displayTitle}</h4>
                      <div className="hstack gap-3 flex-wrap">
                        <div className="d-flex align-items-center gap-2">
                          <span className="text-muted">Status:</span>
                          <StatusBadge
                            value={record.status}
                            map={MaterialRequestStatusMap}
                          />
                        </div>
                        {record.created_at ? (
                          <>
                            <div className="vr" />
                            <div>
                              Created:{" "}
                              <span className="fw-medium">
                                <DateTime date={record.created_at} />
                              </span>
                            </div>
                          </>
                        ) : null}
                        {record.deleted_at ? (
                          <>
                            <div className="vr" />
                            <span
                              className="badge bg-danger"
                              title={record.deleted_at}
                            >
                              Deleted
                            </span>
                          </>
                        ) : null}
                      </div>
                    </div>
                  </div>
                  <div className="col-md-auto">
                    <div className="hstack gap-2 flex-wrap justify-content-md-end">
                      {canEditMr ? (
                        <Link
                          to={`/inventory-management/material-requests/${record.id}/edit`}
                          className="btn btn-light btn-sm border"
                        >
                          <i className="ri-pencil-line align-bottom me-1" />
                          Edit
                        </Link>
                      ) : null}
                      {canApproveRejectUi && canApprove ? (
                        <Button
                          type="button"
                          color="success"
                          size="sm"
                          className="btn-label"
                          onClick={() => setApproveOpen(true)}
                        >
                          <i className="ri-check-line label-icon align-middle fs-14" />
                          Approve
                        </Button>
                      ) : null}
                      {(canApproveRejectUi && canReject) || canCancelMr || canDeleteMr ? (
                        <UncontrolledDropdown>
                          <DropdownToggle
                            type="button"
                            className="btn btn-light btn-sm border"
                          >
                            <i className="ri-more-2-fill align-middle" />
                          </DropdownToggle>
                          <DropdownMenu end>
                            {canApproveRejectUi && canReject ? (
                              <DropdownItem
                                className="text-danger"
                                onClick={() => setRejectOpen(true)}
                              >
                                <i className="ri-close-line align-bottom me-2" />
                                Reject
                              </DropdownItem>
                            ) : null}
                            {canCancelMr ? (
                              <DropdownItem
                                className="text-danger"
                                onClick={() => setCancelOpen(true)}
                              >
                                <i className="ri-forbid-line align-bottom me-2" />
                                Cancel request
                              </DropdownItem>
                            ) : null}
                            {canDeleteMr ? (
                              <DropdownItem
                                className="text-danger"
                                onClick={() => setDeleteOpen(true)}
                              >
                                <i className="ri-delete-bin-line align-bottom me-2" />
                                Delete
                              </DropdownItem>
                            ) : null}
                          </DropdownMenu>
                        </UncontrolledDropdown>
                      ) : null}
                    </div>
                  </div>
                </Row>

                <Nav className="nav-tabs-custom border-bottom-0" role="tablist">
                  <NavItem>
                    <NavLink
                      className={classnames(
                        { active: activeTab === "1" },
                        "text-body fw-semibold",
                      )}
                      onClick={(e) => {
                        e.preventDefault();
                        setTabInUrl("1");
                      }}
                      href="#"
                    >
                      Overview
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={classnames(
                        { active: activeTab === "2" },
                        "text-body fw-semibold",
                      )}
                      onClick={(e) => {
                        e.preventDefault();
                        setTabInUrl("2");
                      }}
                      href="#"
                    >
                      Line items
                    </NavLink>
                  </NavItem>
                </Nav>
              </CardBody>
            </div>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col lg={12}>
          <TabContent activeTab={activeTab} className="text-muted">
            <TabPane tabId="1">
              <OverviewTab />
            </TabPane>
            <TabPane tabId="2">
              <LineItemsTab />
            </TabPane>
          </TabContent>
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default Section;
